import { useMemo } from 'react'

import { matchFilterNumber, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { CampaignPortalModel, CampaignPortalRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['campaignPortals']

type CampaignPortalFilters = {
  campaign_id?: number,
}

type UseCampaignPortalsOptions = {
  filters?: CampaignPortalFilters,
  performHttpRequests?: boolean,
  requestOptions?: CampaignPortalRequestOptions,
}

function useCampaignPortals(options: UseCampaignPortalsOptions) {
  const { filters = {}, requestOptions } = options
  const {
    campaign_id: filterCampaignId,
  } = filters

  const {
    updatedEntities: { campaignPortals: campaignPortalsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { campaignPortals } = useSelector(reduxState => reduxState.entities)

  const filteredCampaignPortals = useMemo(() => {
    const filtered = Object.values(campaignPortals).filter((campaignPortal: CampaignPortalModel) => {
      const { campaign_id } = campaignPortal

      const matchCampaignId = matchFilterNumber(campaign_id, filterCampaignId)

      return matchCampaignId
    })

    return sortArrayBy(filtered, 'asc')
  }, [campaignPortalsUpdatedAt, JSON.stringify(filters)])

  const filteredCampaignPortalsCount = filteredCampaignPortals.length
  const hasFilteredCampaignPortals = !!filteredCampaignPortalsCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredCampaignPortalsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingCampaignPortals } = useReduxAction(
    'campaignPortals',
    'loadCampaignPortals',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  const activePortalIds = filteredCampaignPortals.map(
    campaignPortal => campaignPortal.portal_id,
  )

  return {
    activePortalIds,
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredCampaignPortals,
    filteredCampaignPortalsCount,
    hasFilteredCampaignPortals,
    loading: loadingCampaignPortals,
  }
}

export type UseCampaignPortalsPayload = ReturnType<typeof useCampaignPortals>

export default useCampaignPortals
